import InstitutionLogo from '@/components/cashflow/InstitutionLogo';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { Plus } from '@/components/common/icons';
import { groupHoverStyleProps } from '@/components/common/styleHelpers';
import {
    EyebrowHeader,
    Header,
    LinkText,
    Paragraph,
    Text,
} from '@/components/common/Typography';
import usePlaid from '@/components/plaid/usePlaid';
import { Box, Divider } from '@chakra-ui/react';
import { AnyFinancialEntity, getName } from '@common/financialEntities';
import { useFinancialEntitiesContext } from '@common/react/financialEntities';
import { useBudgetOnboardingContext } from './BudgetOnboardingContext';
import { BudgetOnboardingLayout } from './BudgetOnboardingLayout';

export const BudgetOnboardingLinkAccounts = () => {
    const { fes } = useFinancialEntitiesContext();
    const { goToNextStep } = useBudgetOnboardingContext();

    if (fes.loading || !fes.data) {
        return <></>;
    }

    const creditCards = fes.data.financialEntities.filter(
        (a) => a.info.feType === 'CreditCard'
    );
    const otherAccounts = fes.data.financialEntities.filter(
        (a) =>
            a.info.feType !== 'CreditCard' &&
            a.info.feType !== 'PlentyGoalAccount'
    );

    return (
        <BudgetOnboardingLayout
            onContinue={goToNextStep}
            isContinueDisabled={fes.data.financialEntities.length === 0}
            isSubmitting={false}
            buttonText="Continue"
            onClickEvent="Budget Onboarding Link Accounts Continue Button Pressed"
        >
            <FlexCol>
                <FlexCol gap={4}>
                    <Header>Link key cards + bank accounts</Header>
                    <Paragraph color="textMuted">
                        We&apos;ll pull in the transactions from linked accounts
                        to help you jump start your budget.
                    </Paragraph>
                </FlexCol>
                <FlexCol mt={12} gap={8}>
                    <AccountSection
                        title="Cards"
                        accounts={creditCards}
                        addAccountLabel="Add a credit card"
                    />
                    <Divider />
                    <AccountSection
                        title="Accounts"
                        accounts={otherAccounts}
                        addAccountLabel="Add a bank account"
                    />
                </FlexCol>
            </FlexCol>
        </BudgetOnboardingLayout>
    );
};

const AccountSection = ({
    title,
    accounts,
    addAccountLabel,
}: {
    title: string;
    accounts: AnyFinancialEntity[];
    addAccountLabel: string;
}) => {
    const { open } = usePlaid();

    return (
        <FlexCol mt="3xl" gap={6}>
            <Text fontSize="lg">{title}</Text>
            <FlexCol gap={4}>
                {accounts.map((fe) => (
                    <FinancialEntityRow key={fe.id} financialEntity={fe} />
                ))}
                <FlexRow
                    gap={4}
                    align="center"
                    onClick={open}
                    cursor="pointer"
                    mt={2}
                    role="group"
                >
                    <Box
                        px="4px"
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius={8}
                    >
                        <Plus color="blue.500" size="sm" />
                    </Box>

                    <LinkText
                        {...groupHoverStyleProps({
                            textDecoration: 'underline',
                        })}
                    >
                        {addAccountLabel}
                    </LinkText>
                </FlexRow>
            </FlexCol>
        </FlexCol>
    );
};

const FinancialEntityRow = ({
    financialEntity,
}: {
    financialEntity: AnyFinancialEntity;
}) => {
    return (
        <FlexRow gap={4} align="center">
            <InstitutionLogo
                financialEntity={{
                    name: getName(financialEntity),
                    institutionLogo: financialEntity.institution?.logo,
                }}
            />
            <FlexCol>
                <Text fontWeight="medium">{getName(financialEntity)}</Text>
                <EyebrowHeader>
                    {'....'}
                    {financialEntity.mask}
                </EyebrowHeader>
            </FlexCol>
        </FlexRow>
    );
};
